/**
 * +detail: props for the quiet country's points of interest and the farm: rough menhirs, a toppled
 * giant statue (plinth with feet, torso, head, hand), broken roadside statues, a hermit's hut, a
 * wayside shrine, a torn tent and a wrecked cart, a bedroll, a woodpile, a drying rack, a rowboat
 * and scarecrows. Pale ruin stone matches outdoor/valley.ts; built things keep their outline.
 */
import * as THREE from 'three';
import { OUT, type Gen, combine, paint, registerOutdoorProcs, rockGeo, xf } from '../procs';
import { type RGB3, box, clamp01, cyl, displaceShared, mix, puff, rgb, scale3, tint } from './kit';

const PALE = rgb(0xe8e3d6);
const PALE_SHADE = rgb(0xa9a8a2);
const MOSS = 0x56803a;

/** Pale weathered stone: darker low down, a little facet noise. */
const pale = (y0: number, y1: number) => (x: number, y: number, z: number, _nx: number, ny: number): RGB3 => {
  const t = clamp01((y - y0) / Math.max(0.01, y1 - y0));
  const c = mix(PALE_SHADE, PALE, t * 0.8 + clamp01(ny) * 0.2);
  return scale3(c, (0.74 + 0.3 * t) * (0.95 + 0.08 * Math.sin(x * 4.1 + z * 3.3 + y * 2.2)));
};
const mossCap = (r: number, rnd: () => number, at: RGB3, squash = 0.32): THREE.BufferGeometry => paint(xf(puff(r, rnd, 0.3, 7, 5, 0.5), at, [0, rnd() * 3, 0], [1, squash, 1]), MOSS, [0.85, 1.15]);

/** A rough menhir: a tall leaning slab of grey-blue stone, faceted, with moss and lichen (nature: no outline). */
const menhir: Gen = (rnd) => {
  const h = 3.4 + rnd() * 1.8;
  const g = new THREE.BoxGeometry(1.3, h, 0.8, 2, 4, 1).toNonIndexed();
  const ph = rnd() * 5;
  displaceShared(g, (v) => {
    const t = (v.y + h / 2) / h;
    const k = 1 - 0.35 * t + 0.08 * Math.sin(t * 7 + ph);
    v.x = v.x * k + (rnd() - 0.5) * 0.12;
    v.z = v.z * k + (rnd() - 0.5) * 0.1;
    if (t > 0.98) v.y -= rnd() * 0.5;
  });
  g.translate(0, h / 2 - 0.2, 0).rotateZ((rnd() - 0.5) * 0.16).rotateX((rnd() - 0.5) * 0.1);
  const dark = rgb(0x5f6b7e);
  const mid = rgb(0x8f9aaa);
  const lichen = rgb(0x8fa060);
  const parts = [tint(g, (x, y, z, _nx, ny) => (ny > 0.6 ? rgb(MOSS) : Math.sin(x * 5 + y * 3 + z * 4 + ph) > 0.9 ? lichen : mix(dark, mid, clamp01(y / h + 0.15))), true)];
  parts.push(paint(xf(puff(0.8, rnd, 0.25, 7, 4, 0.5), [0.1, 0.05, 0.1], [0, 0, 0], [1.2, 0.3, 1.0]), MOSS, [0.8, 1.05]));
  return combine(parts);
};

/** The Stone King's plinth: a stepped base with the statue's feet and shins still standing. */
const statuePlinth: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [tint(box(4.2, 0.8, 3.4), pale(0, 0.8), true), tint(xf(box(3.4, 0.9, 2.8), [0, 0.8, 0]), pale(0.6, 1.7), true)];
  for (const sx of [-0.7, 0.75]) {
    const shin = new THREE.CylinderGeometry(0.42, 0.55, 2.2 + rnd() * 0.9, 7, 2).toNonIndexed();
    displaceShared(shin, (v) => (v.y > 0.9 ? (v.y -= rnd() * 0.6) : undefined));
    parts.push(tint(xf(box(0.9, 0.5, 1.5), [sx, 1.7, 0.15]), pale(1.6, 2.2), true));
    parts.push(tint(shin.translate(sx, 2.2 + 1.1, -0.1), pale(1.7, 4.4), true));
  }
  parts.push(mossCap(1.1, rnd, [-1.2, 1.7, 1.0]), mossCap(0.9, rnd, [1.4, 0.8, -1.4]));
  return combine(parts);
};

/** The Stone King's torso lying on its back along local z (chest, shoulders, an arm). */
const statueTorso: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  const body = puff(1.8, rnd, 0.06, 10, 7, 0.65).scale(1.05, 0.62, 2.3).translate(0, 0.9, 0);
  parts.push(tint(body, pale(0, 2.2)));
  parts.push(tint(xf(new THREE.SphereGeometry(0.9, 8, 6).scale(1, 0.7, 1.2), [1.55, 0.75, 2.6]), pale(0, 1.8)));
  parts.push(tint(xf(new THREE.SphereGeometry(0.9, 8, 6).scale(1, 0.7, 1.2), [-1.55, 0.75, 2.6]), pale(0, 1.8)));
  // A broken arm lying along the side, the neck stump.
  parts.push(tint(xf(new THREE.CylinderGeometry(0.45, 0.55, 3.6, 7).rotateX(Math.PI / 2), [2.1, 0.5, 0.6]), pale(0, 1.2), true));
  parts.push(tint(xf(cyl(0.6, 0.7, 0.6, 8), [0, 1.3, 3.8], [Math.PI / 2 - 0.2, 0, 0]), pale(0.5, 2.0), true));
  // The belt and a carved chest plate.
  parts.push(tint(xf(new THREE.TorusGeometry(1.55, 0.14, 4, 14).scale(1, 0.62, 1), [0, 0.95, -1.4], [Math.PI / 2, 0, 0]), pale(0, 2), true));
  parts.push(mossCap(1.4, rnd, [0.3, 1.9, -0.4], 0.28), mossCap(0.9, rnd, [-0.9, 1.6, 1.8], 0.3));
  for (let i = 0; i < 4; i++) parts.push(tint(rockGeo(0.35 + rnd() * 0.3, rnd, 0.7).translate((rnd() - 0.5) * 5, 0, (rnd() - 0.5) * 7), pale(-0.2, 0.6), true));
  return combine(parts);
};

/** The Stone King's head on its side: a crowned, bearded face half sunk in the grass. */
const statueHead: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  parts.push(tint(puff(1.55, rnd, 0.05, 10, 8, 0.75).scale(0.9, 1.0, 1.0).translate(0, 1.1, 0), pale(0, 2.6)));
  // Brow, nose, beard (facing +z).
  parts.push(tint(xf(box(1.6, 0.3, 0.4), [0, 1.5, 1.25]), pale(0.5, 2.5), true));
  parts.push(tint(xf(new THREE.ConeGeometry(0.28, 0.6, 4).rotateX(Math.PI / 2), [0, 1.15, 1.55]), pale(0.5, 2.5), true));
  parts.push(tint(xf(new THREE.ConeGeometry(0.8, 1.4, 6).rotateX(Math.PI), [0, 0.4, 1.0]), pale(-0.3, 1.5), true));
  // A crown of short spikes round the top.
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * Math.PI * 2;
    parts.push(tint(xf(new THREE.ConeGeometry(0.2, 0.7, 4), [Math.cos(a) * 1.05, 2.35, Math.sin(a) * 1.05], [Math.sin(a) * 0.3, 0, -Math.cos(a) * 0.3]), pale(1.5, 3.2), true));
  }
  parts.push(mossCap(1.0, rnd, [-0.4, 2.3, -0.3], 0.35));
  const g = combine(parts);
  // Lying on its side.
  g.rotateZ(Math.PI / 2 - 0.25).translate(1.0, -0.15, 0);
  return g;
};

/** The Stone King's hand reaching up out of the grass. */
const statueHand: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [tint(xf(puff(0.9, rnd, 0.06, 8, 6, 0.6), [0, 0.5, 0], [0, 0, 0], [1.1, 0.8, 1.3]), pale(0, 1.4))];
  for (let i = 0; i < 4; i++) {
    const a = -0.6 + i * 0.4;
    const L = 1.4 + (i === 1 || i === 2 ? 0.4 : 0);
    parts.push(tint(xf(cyl(0.18, 0.22, L, 6), [Math.sin(a) * 0.55, 0.9, 0.3], [-0.35, 0, a * 0.4]), pale(0.6, 2.5), true));
  }
  parts.push(tint(xf(cyl(0.2, 0.24, 1.0, 6), [0.85, 0.6, 0], [0, 0, -0.9]), pale(0.4, 1.6), true));
  parts.push(mossCap(0.7, rnd, [0, 0.1, -0.4], 0.3));
  return combine(parts);
};

/** A roadside statue snapped at the waist: robed legs on a plinth, the upper half fallen beside it. */
const statueBroken: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [tint(box(1.6, 0.5, 1.6), pale(0, 0.5), true)];
  const robe = new THREE.CylinderGeometry(0.45, 0.62, 1.9, 8, 2).toNonIndexed();
  displaceShared(robe, (v) => (v.y > 0.9 ? (v.y -= rnd() * 0.5) : undefined));
  parts.push(tint(robe.translate(0, 0.5 + 0.95, 0), pale(0.3, 2.4), true));
  const upper = new THREE.CylinderGeometry(0.42, 0.46, 1.5, 8).toNonIndexed();
  parts.push(tint(xf(upper, [1.5, 0.4, 0.4], [0.2, 0.5, Math.PI / 2]), pale(-0.2, 1.0), true));
  parts.push(tint(xf(new THREE.SphereGeometry(0.38, 7, 5), [2.5, 0.38, 0.8]), pale(0, 0.8), true));
  parts.push(mossCap(0.6, rnd, [0, 2.3, 0], 0.4), mossCap(0.7, rnd, [1.3, 0.75, 0.4], 0.3));
  return combine(parts);
};

/** The hermit's hut: a low stone-and-timber hut with a sagging thatch roof, a door at +z. */
const hut: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  const W = 4.6;
  const D = 4.0;
  parts.push(paint(box(W + 0.3, 0.6, D + 0.3), OUT.stone, [0.65, 0.95]));
  parts.push(paint(xf(box(W, 2.1, D), [0, 0.5, 0]), 0xd8c8a8, [0.8, 1.05]));
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) parts.push(paint(xf(box(0.26, 2.4, 0.26), [sx * (W / 2), 0.4, sz * (D / 2)]), OUT.timber, [0.7, 1]));
  parts.push(paint(xf(box(1.0, 1.7, 0.12), [-0.6, 0.5, D / 2 + 0.06]), OUT.woodDark, [0.7, 1]));
  parts.push(paint(xf(box(0.7, 0.6, 0.08), [1.2, 1.4, D / 2 + 0.05]), 0x2c3a52, [1, 1]));
  parts.push(paint(xf(box(0.72, 0.62, 0.05), [1.2, 1.4, D / 2 + 0.07]), 0xffd890, [1.0, 1.1]));
  // Thatch: a thick, rounded, sagging roof.
  const roof = new THREE.CylinderGeometry(0.01, 3.9, 2.6, 4, 3).rotateY(Math.PI / 4).toNonIndexed();
  displaceShared(roof, (v) => {
    v.x *= 1.25;
    v.y += -0.25 * Math.sin(((v.x + 3) / 6) * Math.PI) * clamp01(1 - v.y);
    v.x += (rnd() - 0.5) * 0.08;
  });
  roof.computeVertexNormals();
  parts.push(paint(roof.translate(0, 2.6 + 1.3, 0), OUT.roofThatch, [0.7, 1.08]));
  parts.push(mossCap(0.9, rnd, [1.0, 3.6, -0.8], 0.4));
  // A crooked chimney pipe.
  parts.push(paint(xf(cyl(0.2, 0.24, 1.6, 6), [-1.4, 3.2, -0.8], [0, 0, 0.1]), 0x5a5a5e, [0.7, 1]));
  return combine(parts);
};

/** A wayside shrine: a small roofed niche on a stone post with a figure and a candle (+z front). */
const shrine: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [tint(box(1.4, 0.4, 1.2), pale(0, 0.4), true), tint(xf(box(0.9, 1.1, 0.8), [0, 0.4, 0]), pale(0.2, 1.5), true)];
  parts.push(tint(xf(box(1.1, 1.1, 0.8), [0, 1.5, 0]), pale(1.4, 2.6), true));
  parts.push(paint(xf(box(0.75, 0.8, 0.2), [0, 1.65, 0.32]), 0x2a3038, [1, 1]));
  parts.push(tint(xf(new THREE.CylinderGeometry(0.12, 0.2, 0.6, 6), [0, 1.65, 0.28]), pale(1.6, 2.4), true));
  parts.push(tint(xf(new THREE.SphereGeometry(0.11, 6, 4), [0, 2.35, 0.28]), pale(1.6, 2.4)));
  for (const sz of [-1, 1]) parts.push(paint(xf(new THREE.BoxGeometry(1.5, 0.12, 0.8), [0, 2.85, sz * 0.3], [sz * 0.6, 0, 0]), OUT.roofRed, [0.8, 1.05]));
  parts.push(paint(xf(cyl(0.05, 0.05, 0.18, 5), [0.25, 1.65, 0.4]), 0xf4ead0, [1.1, 1.2]));
  parts.push(mossCap(0.6, rnd, [0.3, 0.42, -0.3], 0.3));
  for (let i = 0; i < 5; i++) parts.push(paint(xf(new THREE.IcosahedronGeometry(0.06, 0), [-0.5 + i * 0.25, 0.45, 0.65]), [0xffffff, 0xf2a0c8, 0xf8d050, 0x8ab8ff, 0xff7a5a][i]!, [1, 1.1]));
  return combine(parts);
};

/** An abandoned tent: a sagging, torn ridge tent with one pole down. */
const tentTorn: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  const L = 3.2;
  for (const sx of [-1, 1]) {
    const g = new THREE.PlaneGeometry(1.75, L, 2, 4).toNonIndexed();
    displaceShared(g, (v) => {
      v.z = Math.sin((v.y / L + 0.5) * Math.PI) * 0.25 + (rnd() - 0.5) * 0.06;
    });
    g.rotateX(-Math.PI / 2).rotateZ(sx * 0.95).translate(sx * 0.55, 0.8, 0);
    // Sag toward the fallen end.
    displaceShared(g, (v) => {
      v.y *= 0.65 + 0.35 * clamp01((v.z + L / 2) / L);
    });
    g.computeVertexNormals();
    parts.push(paint(g, sx > 0 ? 0x9c8460 : 0x7e6a4c, [0.7, 1.0]));
    const back = g.clone().scale(1, 1, 1);
    back.index = null;
    const p = back.attributes['position'] as THREE.BufferAttribute;
    for (let i = 0; i < p.count; i += 3) {
      const x = p.getX(i), y = p.getY(i), z = p.getZ(i);
      p.setXYZ(i, p.getX(i + 1), p.getY(i + 1), p.getZ(i + 1));
      p.setXYZ(i + 1, x, y, z);
    }
    back.computeVertexNormals();
    parts.push(paint(back, 0x6a5a40, [0.6, 0.9]));
  }
  parts.push(paint(xf(box(0.1, 1.6, 0.1), [0, 0, L / 2]), OUT.woodDark, [0.7, 1]));
  parts.push(paint(xf(new THREE.CylinderGeometry(0.05, 0.05, 1.8, 5).rotateZ(1.2), [0.6, 0.25, -L / 2 - 0.2]), OUT.woodDark, [0.8, 1]));
  return combine(parts);
};

/** A wrecked cart: tipped on its side, a broken wheel, spilled sacks. */
const cartWreck: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [paint(xf(box(1.6, 0.6, 2.6), [0.2, 0, 0], [0, 0, 1.25]), OUT.wood, [0.65, 1.0])];
  parts.push(paint(xf(new THREE.TorusGeometry(0.55, 0.09, 6, 12).rotateY(Math.PI / 2), [-0.4, 1.3, -0.4], [0, 0, 1.25]), OUT.woodDark, [0.8, 1]));
  parts.push(paint(xf(new THREE.TorusGeometry(0.55, 0.09, 6, 7, Math.PI * 1.2).rotateX(Math.PI / 2), [1.4, 0.09, 0.9]), OUT.woodDark, [0.8, 1]));
  for (let i = 0; i < 3; i++) parts.push(paint(xf(puff(0.32, rnd, 0.15, 7, 5, 0.3), [1.3 + i * 0.5, 0.2, -0.7 + i * 0.4], [0, 0, 0], [1, 0.75, 1.25]), 0xcbb488, [0.75, 1.05]));
  parts.push(paint(xf(box(0.08, 0.08, 1.6), [0.9, 0.05, 2.0], [0, 0.3, 0]), OUT.woodDark, [1, 1]));
  return combine(parts);
};

/** A bedroll by a fire: a rolled blanket and a pack. */
const bedroll: Gen = (rnd) =>
  combine([
    paint(xf(box(0.9, 0.12, 1.9), [0, 0, 0]), 0x8a3a3a, [0.85, 1.05]),
    paint(xf(new THREE.CylinderGeometry(0.22, 0.22, 0.9, 8).rotateZ(Math.PI / 2), [0, 0.22, -0.95]), 0x6e5a3e, [0.8, 1]),
    paint(xf(puff(0.35, rnd, 0.15, 7, 5, 0.4), [0.7, 0.3, 0.4], [0, 0, 0], [1, 1.1, 0.8]), 0x7a6040, [0.7, 1.05]),
  ]);

/** A woodpile: split logs stacked between two posts. */
const woodpile: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  for (let row = 0; row < 3; row++)
    for (let i = 0; i < 5 - row; i++) {
      const g = new THREE.CylinderGeometry(0.17, 0.17, 1.1, 6).rotateX(Math.PI / 2);
      parts.push(paint(xf(g, [-0.75 + i * 0.36 + row * 0.18, 0.17 + row * 0.3, (rnd() - 0.5) * 0.12]), i % 2 ? 0x7a5236 : 0x8c6040, [0.75, 1.05]));
      parts.push(paint(xf(new THREE.CylinderGeometry(0.15, 0.15, 0.02, 6).rotateX(Math.PI / 2), [-0.75 + i * 0.36 + row * 0.18, 0.17 + row * 0.3, 0.56]), 0xd8b98a, [1, 1]));
    }
  for (const sx of [-1.1, 1.1]) parts.push(paint(xf(box(0.12, 1.2, 0.12), [sx, 0, 0]), OUT.woodDark, [0.7, 1]));
  return combine(parts);
};

/** A fish-drying rack: two crossed-pole frames, a cross bar, a few fish hanging. */
const dryingRack: Gen = () => {
  const parts: THREE.BufferGeometry[] = [];
  for (const sx of [-1.2, 1.2]) for (const s of [-1, 1]) parts.push(paint(xf(cyl(0.05, 0.06, 2.0, 5), [sx, 0, 0], [s * 0.25, 0, 0]), OUT.woodDark, [0.7, 1]));
  parts.push(paint(xf(new THREE.CylinderGeometry(0.05, 0.05, 2.7, 5).rotateZ(Math.PI / 2), [0, 1.85, 0]), OUT.wood, [0.9, 1]));
  for (let i = 0; i < 5; i++) parts.push(paint(xf(new THREE.ConeGeometry(0.1, 0.55, 4).rotateX(Math.PI), [-0.9 + i * 0.45, 1.5, 0], [0, 0, 0], [1, 1, 0.4]), i % 2 ? 0x8a9aa8 : 0xa0aab4, [0.8, 1.1]));
  return combine(parts);
};

/** A rowboat pulled up on the bank (bow +z). */
const rowboat: Gen = (rnd) => {
  const hull = new THREE.SphereGeometry(1, 10, 6, 0, Math.PI * 2, Math.PI / 2, Math.PI / 2).scale(0.75, 0.5, 1.9).toNonIndexed();
  displaceShared(hull, (v) => (v.z > 0 ? (v.x *= 1 - clamp01(v.z / 1.9) * 0.6) : undefined));
  hull.computeVertexNormals();
  const parts: THREE.BufferGeometry[] = [paint(hull.translate(0, 0.5, 0), 0x6e4e34, [0.6, 1.0])];
  const inner = new THREE.SphereGeometry(0.92, 10, 6, 0, Math.PI * 2, Math.PI / 2, Math.PI / 2).scale(-0.7, 0.42, 1.8).translate(0, 0.52, 0);
  parts.push(paint(inner, 0x4a3426, [0.7, 0.9]));
  for (const z of [-0.5, 0.5]) parts.push(paint(xf(box(1.3, 0.08, 0.3), [0, 0.32, z]), 0x8a6a46, [0.9, 1]));
  parts.push(paint(xf(new THREE.CylinderGeometry(0.035, 0.035, 2.4, 4).rotateZ(Math.PI / 2 - 0.15), [0.1, 0.45, 0.1], [0, 0.3, 0]), OUT.wood, [0.9, 1]));
  void rnd;
  return combine(parts);
};

/** A scarecrow: a cross of poles, a straw-stuffed coat, a sack head with a floppy hat. */
const scarecrow: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [paint(cyl(0.07, 0.09, 2.6, 5), OUT.woodDark, [0.7, 1])];
  parts.push(paint(xf(new THREE.CylinderGeometry(0.05, 0.05, 1.9, 5).rotateZ(Math.PI / 2), [0, 1.85, 0]), OUT.woodDark, [0.9, 1]));
  parts.push(paint(xf(new THREE.CylinderGeometry(0.32, 0.45, 1.0, 6), [0, 1.45, 0]), rnd() < 0.5 ? 0x5a6ea0 : 0x8a4a3a, [0.75, 1.05]));
  for (const sx of [-1, 1]) parts.push(paint(xf(new THREE.CylinderGeometry(0.13, 0.16, 0.85, 5).rotateZ(Math.PI / 2), [sx * 0.62, 1.85, 0]), 0x6a7a50, [0.8, 1]));
  for (const sx of [-1, 1]) parts.push(paint(xf(new THREE.ConeGeometry(0.12, 0.3, 5).rotateZ(sx * Math.PI / 2), [sx * 1.15, 1.85, 0]), OUT.hay, [0.9, 1.1]));
  parts.push(paint(xf(new THREE.SphereGeometry(0.28, 7, 5), [0, 2.3, 0]), 0xd8c8a0, [0.85, 1.05]));
  parts.push(paint(xf(new THREE.CylinderGeometry(0.5, 0.52, 0.06, 9), [0, 2.48, 0], [0.15, 0, 0.1]), 0x5a4a30, [0.85, 1]));
  parts.push(paint(xf(new THREE.ConeGeometry(0.28, 0.42, 7), [0, 2.5, 0], [0.15, 0, 0.1]), 0x5a4a30, [0.85, 1.05]));
  parts.push(paint(xf(new THREE.ConeGeometry(0.3, 0.4, 6).rotateX(Math.PI), [0, 0.85, 0]), OUT.hay, [0.85, 1.05]));
  return combine(parts);
};

registerOutdoorProcs({
  mossrock_menhir: menhir,
  statue_plinth: statuePlinth,
  statue_torso: statueTorso,
  statue_head: statueHead,
  statue_hand: statueHand,
  statue_broken: statueBroken,
  hut,
  shrine,
  tent_torn: tentTorn,
  cart_wreck: cartWreck,
  bedroll,
  woodpile,
  drying_rack: dryingRack,
  rowboat,
  scarecrow,
});
